import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductImage from "@/components/ProductImage";
import { useCart } from "@/context/CartContext";
import { getBrand, getCategory, formatPrice, specChips, displayPrice, categoryAccent } from "@/data/catalog";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const brand = getBrand(product.brandSlug);
  const category = getCategory(product.categorySlug);
  const chips = specChips(product);
  const dp = displayPrice(product);
  const warm = categoryAccent(product.categorySlug) === "warm";

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(dp.mode === "show" ? "Added to cart" : "Added to your quote request", { description: product.name });
  };

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_18px_40px_-20px_rgba(15,23,42,0.35)]"
    >
      <span className={`h-1 w-full ${warm ? "bg-orange-500" : "bg-sky-600"}`} />

      <Link to={`/product/${product.slug}`} className="relative block aspect-square overflow-hidden border-b border-slate-100">
        <ProductImage product={product} />
        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          {product.clearance && (
            <span className="rounded-sm bg-gradient-to-r from-orange-600 to-amber-600 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">Clearance</span>
          )}
          {product.featured && !product.clearance && (
            <span className="rounded-sm border border-sky-700/60 bg-sky-950 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest text-sky-300">Featured</span>
          )}
        </div>
        {product.specs?.refrigerant && (
          <span className="absolute right-3 top-3 rounded-sm border border-cyan-200 bg-cyan-50 px-2 py-0.5 font-mono text-[10px] font-bold text-cyan-800">{product.specs.refrigerant}</span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700">{brand?.name}</span>
          <span className="truncate pl-2 font-mono text-[10px] text-slate-400">{category?.name}</span>
        </div>

        <Link to={`/product/${product.slug}`} className="mt-1.5 line-clamp-2 font-heading text-base font-bold leading-snug tracking-tight text-slate-900 transition-colors duration-150 hover:text-sky-700">
          {product.name}
        </Link>
        <div className="mt-1 font-mono text-xs text-slate-500">{product.model}</div>

        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {chips.map((c, i) => (
              <span key={i} className="rounded-sm bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-medium text-slate-600">{c}</span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-4">
          <div className="mb-3 flex min-h-[2rem] items-baseline gap-2">
            {dp.mode === "show" ? (
              <>
                <span className="font-heading text-xl font-black tracking-tight text-slate-900">{formatPrice(dp.price)}</span>
                {dp.original && dp.original > dp.price && (
                  <span className="text-sm text-slate-400 line-through">{formatPrice(dp.original)}</span>
                )}
              </>
            ) : (
              <span className="inline-flex flex-col leading-tight">
                <span className="font-heading text-sm font-bold text-slate-800">Request Price</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Fast quote · no account</span>
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm" className="flex-1 rounded-sm border-slate-300 font-semibold" data-testid={`view-product-${product.slug}`}>
              <Link to={`/product/${product.slug}`}>View Specs</Link>
            </Button>
            <Button
              size="sm"
              className={`flex-1 rounded-sm font-semibold text-white ${warm ? "bg-orange-600 hover:bg-orange-700" : "bg-sky-700 hover:bg-sky-800"}`}
              data-testid={`add-to-cart-${product.slug}`}
              onClick={handleAdd}
            >
              {dp.mode === "show" ? <><ShoppingCart className="h-4 w-4" /> Add</> : <>Quote <ArrowRight className="h-3.5 w-3.5" /></>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
